import { z } from 'zod'
import { PublicatieId } from './id.js'
import { Person } from './moment.js'

/**
 * Wie er handelt: de kant en de rol uit het token van de gastheer (B18, R-155a).
 *
 * Dit is het vierde ingrediënt van de projectie, naast de bevroren definitie, de
 * deling en het laag-eigenaarschap (ontwerp 5.2). Beide waarden komen als
 * verplichte claim mee en worden nooit uit de invoer afgeleid; de uitgever
 * begrenst wat ze mogen zeggen, dus een token van het portaal-IdP draagt nooit
 * `intern` en nooit `toetser`. Ontbreekt er een, dan wordt het token geweigerd
 * en niet met een standaardwaarde aangevuld (ontwerp 3.3).
 */

/**
 * Aan welke kant iemand staat (R-174, R-66a).
 *
 * De kant stuurt twee dingen: wie een deling mag maken (R-64d) en wie welke
 * delingen ziet (R-66a). Hij wordt niet afgeleid uit het gastheervoorvoegsel van
 * `Persoon`, want een NWO-medewerker kan zelf een formulier invullen (R-138) en
 * staat dan intern in de invullerrol: het voorvoegsel zegt welke gastheer, niet
 * welke kant.
 */
export const Side = z.enum(['intern', 'extern'])
export type Side = z.infer<typeof Side>

/**
 * In welke rol iemand handelt, en daarmee in welke laag hij schrijft (R-172b).
 *
 * Er zijn er precies twee, want dat zijn de twee lagen binnen één definitie
 * (D-23): de invuller schrijft in de invoerlaag, de toetser in de toetslaag. Een
 * tweede toetser komt de toetslaag binnen op deze rol en niet via een deling.
 *
 * Dit is niet het rollenmodel dat U6 verbiedt. U6 gaat over de definitie:
 * sectierechten horen bij de deling en niet bij het formulier (R-67). Deze rol
 * staat in het token, deelt uit zichzelf geen enkel sectierecht uit en wijst
 * alleen de laag aan.
 */
export const Role = z.enum(['invuller', 'toetser'])
export type Role = z.infer<typeof Role>

/**
 * Eén ingang van een pod: de naam van de uitgever en de kant die daar binnenkomt (R-155a).
 *
 * De naam is de gastheer uit `RUNTIME_UITGEVERS` en dezelfde als de tak van de inlogdienst, dus
 * `<inlogdienst>/<gastheer>/starten` is waar iemand langs deze ingang inlogt.
 */
export const Entry = z.strictObject({
  gastheer: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
  kant: Side,
})
export type Entry = z.infer<typeof Entry>

/**
 * Langs welke ingangen iemand op een pod inlogt, en bij welke inlogdienst: het antwoord van
 * `GET /ingangen` (R-154, G017, ontwerp 3.3, opdracht 5c).
 *
 * Publiek, want wie het vraagt heeft nog geen token; daarom alleen wat de pagina nodig heeft om per
 * ingang een knop te tonen. De pod eist https voor de inlogdienst (`RUNTIME_INLOGDIENST`); dit
 * schema laat ook http toe, omdat de opzet van de browserflows op de loopback draait.
 */
export const Entries = z.strictObject({
  inlogdienst: z.url({ protocol: /^https?$/ }),
  ingangen: z.array(Entry).min(1),
})
export type Entries = z.infer<typeof Entries>

/**
 * Het merkteken van NWO: wat de externe kant krijgt waar een persoon van de interne kant zou staan
 * (R-66a, B6, opdracht 8 van de reeks toegang via toekenningen).
 *
 * Eén vorm voor de presence en voor elk antwoord van de pod, zodat een lezer hem op één manier
 * herkent. Een object en geen tekst: een `Persoon` is een tekst met een vast patroon, en een vaste
 * tekst als "nwo" op die plek zou elke afnemer die een persoon vergelijkt of toont voor een persoon
 * aanzien. Dit object kan nooit voor een persoon doorgaan, en een strikt schema laat er niets bij.
 */
export const NWO_EMPLOYEE = { anoniem: 'nwo' } as const
export const NwoEmployee = z.strictObject({ anoniem: z.literal('nwo') })
export type NwoEmployee = z.infer<typeof NwoEmployee>

/** Een persoon zoals de externe kant hem in een antwoord krijgt: zichzelf, of het merkteken van NWO. */
export const PersonForExternal = z.union([Person, NwoEmployee])
export type PersonForExternal = z.infer<typeof PersonForExternal>

/**
 * Het algoritme waarmee de inlogdienst een podtoken ondertekent, en het enige dat een pod aanneemt
 * (ontwerp 3.3, R-154, beveiligingsronde van 1 november 2026).
 *
 * Eén plek voor beide kanten: de inlogdienst ondertekent ermee (`apps/inlog/src/keys.ts`) en de pod
 * verifieert met precies deze lijst (`apps/runtime/src/token.ts`). Zonder lijst bij het verifiëren
 * beslist de kop van het token zelf welk algoritme geldt; een symmetrisch algoritme met de publieke
 * sleutel als geheim is de bekende route daarlangs.
 */
export const TOKEN_ALGORITHM = 'ES256'

/**
 * Hoe lang een podtoken hoogstens geldig is, in seconden: twaalf uur.
 *
 * De bovengrens van `INLOG_GELDIGHEID_S`, die de inlogdienst bij het starten afdwingt
 * (`apps/inlog/src/config.ts`); daarboven is het een sessie die een werkdag overleeft. De pod weigert
 * een token dat langer dan dit vanaf nu geldig zou blijven: een vervaltijd ver in de toekomst komt
 * dan niet uit de inlogdienst, of uit een inlogdienst die anders is ingericht dan voorgeschreven.
 */
export const TOKEN_VALIDITY_MAX_S = 12 * 60 * 60

/**
 * Hoeveel een vervaltijd boven {@link TOKEN_VALIDITY_MAX_S} mag liggen, voor een klok van de inlogdienst
 * die voorloopt: vijf minuten, de gangbare speling bij OIDC. De pod en de opslagdienst rekenen er allebei
 * mee.
 */
export const TOKEN_CLOCK_SKEW_S = 5 * 60

/**
 * De claims van een token van de inlogdienst, zoals de pod en sinds 1 november 2026 ook de opslagdienst
 * ze nakijken (R-154, R-155a, R-155e, R-155f). Eén definitie voor beide kanten.
 *
 * **`z.object` en niet `z.strictObject`, met opzet.** Zod filtert onbekende sleutels weg in plaats van
 * ze te weigeren, en dat heeft een claim nodig die later wordt toegevoegd: een pod wordt na het
 * publiceren nooit bijgewerkt (D-36, R-101), dus een uitgever die er een claim bij zet mag geen
 * draaiende pod laten omvallen. `rol`, `laagEigenaar` en `namens` vallen zo ook weg; die staan sinds 9
 * oktober 2026 in het register (R-155b, R-190).
 */
export const PodTokenClaims = z.object({
  /** Verplicht zoals het was, maar niet meer waar de persoon uit komt: bij Entra ID verschilt `sub` per applicatie (R-155e). */
  sub: z.string().min(1),
  /** De vaste sleutel van de persoon (R-155e). De persoon is `<ingang>:<sleutel>`. */
  sleutel: z.string().min(1),
  /** Verplicht: een token zonder vervalclaim is een sessie zonder eind (bijlage B, probleem 8). */
  exp: z.number().int().positive(),
  email: z.email(),
  /** Intern of extern, uit de tak van de inlogdienst en gewogen tegen de uitgever (R-155a). */
  kant: Side,
  /** De publicatie waarvoor het token is uitgegeven (R-155a), uit de doellijst van de inlogdienst. */
  publicatie: PublicatieId,
  /** De beheerrol (R-155f): alleen `true` of afwezig, en alleen langs de interne tak. */
  beheer: z.literal(true).optional(),
})
export type PodTokenClaims = z.infer<typeof PodTokenClaims>

/** De claims die jose als aanwezig eist voordat het schema hierboven ze leest. */
export const POD_TOKEN_REQUIRED_CLAIMS = ['exp', 'sub', 'sleutel', 'email', 'kant', 'publicatie'] as const

/**
 * De ingang die geen uitgever kan zijn (R-155a, R-69): de koppelvlakdienst zet bij een aanmaakverzoek
 * zonder eigenaar `koppelvlak:geen-eigenaar` op de instantie, en die mag nooit bij een ingelogde persoon
 * passen.
 */
export const RESERVED_ENTRY = 'koppelvlak'

/**
 * Een tak van de inlogdienst die tokens uitgeeft, met de grenzen die erbij horen (ontwerp 3.3, 6.9,
 * B18). Dezelfde vorm in `RUNTIME_UITGEVERS` van een pod en in `STORE_INLOG_UITGEVERS` van de
 * opslagdienst, zodat dezelfde waarde in beide past.
 *
 * `gastheer` is het voorvoegsel waarmee de vaste sleutel uit het token een `Persoon` wordt
 * (`portaal:<sleutel>`, R-155e); dat is niet uit de sleutel af te leiden, dus het staat hier.
 */
export const TokenIssuer = z.strictObject({
  gastheer: z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,31}$/, 'een gastheernaam is een slug')
    .refine((gastheer) => gastheer !== RESERVED_ENTRY, {
      message: `de ingang ${RESERVED_ENTRY} is gereserveerd voor de aanduiding zonder eigenaar en kan geen uitgever zijn`,
    }),
  issuer: z.url(),
  /** Waar de publieke sleutels staan. Bij de opslagdienst mag dat het adres binnen het cluster zijn; de issuer blijft het publieke. */
  jwks: z.url(),
  audience: z.string().min(1),
  /** Precies één kant: de kant volgt uit de ingang (R-155a). Een lijst, omdat elk bestaand geheim die vorm had. */
  kanten: z.array(Side).length(1, 'een uitgever heeft precies één kant, want de kant volgt uit de ingang (R-155a)'),
  /** Aangenomen en genegeerd sinds 9 oktober 2026: de rol komt uit de toekenning (R-155a, B18). */
  rollen: z.array(Role).min(1).optional(),
})
export type TokenIssuer = z.infer<typeof TokenIssuer>

/**
 * De header waarin een pod het token van de gebruiker aan de opslagdienst meestuurt, op de routes die een
 * persoon dragen (R-154, besluit van 1 november 2026). Naast `authorization`, die de sleutel van de pod
 * draagt. Een afspraak tussen twee diensten, dus hier en niet in een van beide.
 */
export const USER_TOKEN_HEADER = 'x-eformulieren-gebruiker'
